import { StyleSheet } from 'react-native';
import React, { useEffect, useMemo, useState } from "react";
import { useIsFocused, useRoute } from "@react-navigation/native";
import Text from "theme/Text";
import { useSelector } from "react-redux";
import i18n from "utils/i18n";
import Translate from "utils/translate";
import _ from "lodash";
import { textStyles } from "styles/text";
import LoadingComponent from "components/atoms/Loading";
import ThemedSafeAreaComponentTop from "components/atoms/ThemedSafeAreaTop";
import ThemedLayout from "components/atoms/ThemedLayout";
import ThemedBlock from "components/atoms/ThemedBlock";
import HelperString from 'utils/helper-string'
import FetchCachedEmployee from 'utils/fetch/cached/FetchCachedEmployee'
import { managementConstants } from 'services/redux/management/constants'
import FetchManagementBookingsValidation from 'utils/fetch/management/FetchManagementBookingsValidation'
import BookingsListItem from 'scenes/management/bookings/_components/BookingsListItem'
import DataItem from 'components/atoms/DataItem'
import ThemedList from 'components/atoms/ThemedList'
import VStack from 'components/atoms/stack/VStack'
import { ThemedValues } from 'styles/themed'
import HelperArray from 'utils/helper-array'
import { DataItemStyles } from 'styles/components/atoms/DataItem'

const ManagementBookingsValidateViewScene = () => {
	
	const isFocused = useIsFocused();
	const route = useRoute();
	
	const buildingGuid = _.get( route.params, 'buildingGuid', null );
	
	const _managementBuildings = useSelector( state => {
		return state.management[managementConstants.BUILDINGS];
	} )
	const _managementBuilding = useSelector( state => {
		return state.management[managementConstants.BUILDINGS][buildingGuid];
	} )
	
	const [ loading, setLoading ] = useState( false )
	const [ employees, setEmployees ] = useState( {} )
	const [ licensePlates, setLicensePlates ] = useState( {} )
	
	const fetchBookings = ( { withLoading = true, callback = null } = {} ) => {
		FetchManagementBookingsValidation.fetch({
			building: {
				guid: buildingGuid
			},
			//date: currentDate,
			callbackStart: () => {
				if( withLoading ) {
					setLoading( true )
				}
			},
			callbackSuccess: ( employeeBookings ) => {
				// create the license plate list?
				const employeeList = [];
				let list = {}
				_.forEach( employeeBookings, booking => {
					const licensePlate = booking?.license_plate_item?.license_plate
					
					// Validate the license plate
					if( !licensePlate ) {
						return;
					}
					
					if( !booking.employee ) {
						return;
					}
					
					if( !_.has( list, licensePlate ) ) {
						list[ licensePlate ] = []
					}
					
					list[ licensePlate ].push( booking )
					
					employeeList.push( booking.employee )
				})
				
				setLicensePlates( list )
				setEmployees( employeeList )
				setLoading( false )
				
				if( _.isFunction( callback ) ) {
					callback();
				}
			},
			callbackError: () => {
				setLoading( false )
				
				if( _.isFunction( callback ) ) {
					callback();
				}
			}
		});
	}
	
	useEffect( () => {
		fetchBookings()
	}, [] );
	
	// Collect all employees and add them to the fetch Cache
	useEffect( () => {
		FetchCachedEmployee.fetchEasy( { employee_guids: _.map( employees, ( employee ) => {
			return employee.guid;
		} ) }  );
	}, [ employees ] );
	
	const content = useMemo( () => {
		if( loading ) {
			return (
				<LoadingComponent />
			)
		}
		
		if(  _.size( licensePlates ) === 0 ) {
			return (
				<Text style={ textStyles.textCenter }>
					{ i18n.t(`management:generic.no_results` ) }
				</Text>
			)
		}
		
		return (
			<ThemedBlock
				key={ HelperString.hash( 'render-group' ) }
				shadow={ true }>
				
				<ThemedList
					data={ _.values( licensePlates ) }
					renderItem={ ({ item }) => {
						const booking = _.first( item )
						
						const building = _managementBuildings[ booking.building?.guid ]
						
						const timeslotKindLimitation = _.get( booking, 'timeslot.timeslot_kind.limitation', {} )
						
						// Create license plate data:
						const dataItems = useMemo( () => {
							const licensePlateData = _.get( item[ 0 ], 'license_plate_item', {} )
							
							const itemOptions = {
								license_plate: [],
								car_fuel_type: [
									'car_fuel_type'
								],
								car_height: {
									min: 'car_height_min',
									max: 'car_height_max'
								},
								car_length: {
									min: 'car_length_min',
									max: 'car_length_max'
								},
								car_width: {
									min: 'car_width_min',
									max: 'car_width_max'
								},
								car_weight: {
									min: 'car_weight_min',
									max: 'car_weight_max'
								}
							}
							
							return _.map( itemOptions, ( options, key ) => {
								const itemRenderKey = booking.guid + key
								
								const content = [
									<Text key={`data-item-${ itemRenderKey }`} style={ DataItemStyles.text }>{ licensePlateData[ key ] ?? i18n.t('enums.employee_status.unknown') }</Text>,
								]
								
								// Check if we need to add the min/max values
								let optionValue = null;
								_.forEach( options, (option, name) => {
									const value = timeslotKindLimitation[ option ]
									if( value !== null ) {
										if( _.isInteger( name ) ) {
											optionValue = value;
											return;
										}
										
										if( optionValue === null ) {
											optionValue = []
										}
										
										optionValue.push( `${ name }: ${ value }` )
									}
								})
								
								if( optionValue !== null && !_.isEmpty( optionValue ) ) {
									switch( true ) {
 										case _.isArray( optionValue ):
											content.push(
												<Text key={`data-item-${ itemRenderKey }-option`} style={ DataItemStyles.text }>({ optionValue.join(' / ') })</Text>
											)
											break;
											
										default:
											content.push( <Text key={`data-item-${ itemRenderKey }-option`} style={ DataItemStyles.text }>({ optionValue })</Text> )
											break;
									}
								}
								
								return (
									<DataItem
										key={ HelperString.hash( 'render-group-item', key ) }
										content={ content }>
										
										{ i18n.t( `enums.license.${ key }.title` ) }
									</DataItem>
								)
							})
						}, [ item ] )
						
						return (
							<BookingsListItem
								key={ HelperString.hash( 'render-group-item', item ) }
								reload={ fetchBookings }
								item={{
									...booking.employee,
									bookings: item
								}}>
								
								<DataItem
									key={ HelperString.hash( 'render-group-item-building', building ) }
									content={ Translate.field( building, 'title' ) }>
									
									{ i18n.t('management:form.building.title') }
								</DataItem>
								<DataItem
									key={ HelperString.hash( 'render-group-item-booking-date', building ) }
									content={ HelperArray.naturalJoin( _.map( item, booking => {
										return booking.booking_date;
									} ) ) }>
									
									{ i18n.t( 'constants.date' ) }
								</DataItem>
								
								{ dataItems }
							</BookingsListItem>
						)
					}}
				/>
			</ThemedBlock>
		)
	}, [ loading, licensePlates ] )
	
	return (
		<ThemedSafeAreaComponentTop
			top={ false }>
			
			<ThemedLayout
				topNavigation={ true }
				topNavigationProps={{
					showBackButton: true
				}}
				title={ i18n.t( 'management:menu.bookings_validate' ) +' - '+ Translate.field( _managementBuilding, 'title' ) }>
				
				<VStack
					space={ ThemedValues.stackSpace }>
					
					<ThemedBlock
						overflow={ true }
						transparent
						style={staticStyles.themedBlockAlignSelfCenterMaxWidth680}>
						
						<Text>{ i18n.t('management:pages.bookings.view.bookings_validate.information') }</Text>
					</ThemedBlock>
					
					{ content }
				</VStack>
			</ThemedLayout>
		</ThemedSafeAreaComponentTop>
	);
}

const staticStyles = StyleSheet.create({
	themedBlockAlignSelfCenterMaxWidth680: {
		alignSelf: 'center',
		maxWidth: 680,
	},
});

export default ManagementBookingsValidateViewScene
